import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, inr, fmtDateTime } from "@/lib/api";
import { useAdminList, Spin } from "@/components/admin/AdminOps";

const Stat = ({ label, value, id }) => (
  <div className="card-surface p-5"><p className="text-xs text-slate-500 uppercase tracking-wider font-mono">{label}</p><p className="font-display text-3xl font-bold mt-1" data-testid={id}>{value}</p></div>
);

export const Overview = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!s) return <Spin />;
  const b = s.bookings;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Cash confirmed" value={inr(s.gross_volume)} id="stat-gross" />
        <Stat label="Platform commission (30%)" value={inr(s.platform_commission)} id="stat-commission" />
        <Stat label="Companion payouts paid" value={inr(s.payouts)} id="stat-payouts" />
        <Stat label="Refunds" value={inr(s.refunds)} id="stat-refunds" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {["pending", "accepted", "confirmed", "completed", "disputed"].map((k) => <Stat key={k} label={`${k} bookings`} value={b[k] || 0} id={`stat-${k}`} />)}
        <Stat label="Open tickets" value={s.open_tickets} id="stat-tickets" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div className="card-surface p-5"><b>Companions</b>{Object.entries(s.companions).map(([k, v]) => <p key={k} className="flex justify-between mt-1"><span>{k}</span><span className="font-mono">{v}</span></p>)}</div>
        <div className="card-surface p-5"><b>Users by role</b>{Object.entries(s.users).map(([k, v]) => <p key={k} className="flex justify-between mt-1"><span>{k}</span><span className="font-mono">{v}</span></p>)}</div>
      </div>
    </div>
  );
};

export const AuditLog = () => {
  const [items] = useAdminList("/admin/audit");
  return (
    <div className="overflow-x-auto card-surface" data-testid="admin-audit-log">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left"><tr><th className="p-3">When</th><th className="p-3">Action</th><th className="p-3">Target</th><th className="p-3">Details</th></tr></thead>
        <tbody>
          {items === null && <tr><td className="p-4"><Spin /></td></tr>}
          {(items || []).map((a) => (
            <tr key={a.id} className="border-t" data-testid={`audit-row-${a.id}`}>
              <td className="p-3 whitespace-nowrap">{fmtDateTime(a.at)}</td>
              <td className="p-3 font-mono text-xs">{a.action}</td>
              <td className="p-3 font-mono text-xs">{a.details?.ref || a.target}</td>
              <td className="p-3 text-xs text-slate-500">{Object.entries(a.details || {}).filter(([k]) => k !== "ref").map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</td>
            </tr>
          ))}
          {items && !items.length && <tr><td colSpan={4} className="p-6 text-center text-slate-500">No audit entries yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};


export const Transactions = () => {
  const [items] = useAdminList("/admin/transactions");
  return (
    <div className="overflow-x-auto card-surface">
      <table className="w-full text-sm" data-testid="transactions-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-3">When</th><th className="p-3">Type</th><th className="p-3">Booking</th><th className="p-3">Amount</th><th className="p-3">Commission</th><th className="p-3">Reference</th><th className="p-3">Method</th></tr></thead>
        <tbody>{items === null ? <tr><td className="p-4"><Spin /></td></tr> : items.map((t) => (
          <tr key={t.id} className="border-t"><td className="p-3 whitespace-nowrap">{fmtDateTime(t.created_at)}</td><td className="p-3"><span className={`chip ${t.type.includes("refund") || t.type === "payout" ? "text-sky-700" : "text-emerald-700"}`}>{t.type}</span></td><td className="p-3 font-mono text-xs">{t.booking_ref}</td><td className="p-3 font-mono">{t.type.includes("refund") || t.type === "payout" ? "−" : ""}{inr(t.amount)}</td><td className="p-3 font-mono">{t.commission != null ? inr(t.commission) : "—"}</td><td className="p-3 font-mono text-xs">{t.reference || "—"}</td><td className="p-3 text-xs uppercase">{t.method}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
};

export const Users = () => {
  const [items, load] = useAdminList("/admin/users");
  const [resets, loadResets] = useAdminList("/admin/password-resets");
  const resetPw = async (u) => {
    if (!window.confirm(`Reset the password for ${u.email}? Their current sessions will be signed out.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.user_id || u.id}/reset-password`);
      window.prompt(`Temporary password for ${u.email} — share it only on their registered mobile. They must change it after login.`, data.temporary_password);
      load();
      loadResets();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const patch = async (u, body) => {
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      toast.success("User updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
    {resets?.length > 0 && (
      <div className="card-surface p-4 space-y-2 border-amber-300" data-testid="password-reset-requests">
        <p className="font-semibold">Password reset requests ({resets.length})</p>
        {resets.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t pt-2" data-testid={`reset-request-${r.email}`}>
            <span>{r.name} · {r.email} · <span className="chip">{r.role}</span> · {r.phone ? <a href={`tel:+91${r.phone}`} className="underline font-mono">+91 {r.phone}</a> : "—"} · {fmtDateTime(r.requested_at)}</span>
            <Button size="sm" onClick={() => resetPw(r)} data-testid={`reset-request-btn-${r.email}`}>Reset password</Button>
          </div>
        ))}
      </div>
    )}
    <div className="overflow-x-auto card-surface">
      <table className="w-full text-sm" data-testid="users-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Mobile</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
        <tbody>{items === null ? <tr><td className="p-4"><Spin /></td></tr> : items.map((u) => (
          <tr key={u.id} className="border-t" data-testid={`user-row-${u.email}`}><td className="p-3">{u.name}</td><td className="p-3">{u.email}</td><td className="p-3 font-mono text-xs whitespace-nowrap" data-testid={`user-phone-${u.email}`}>{u.phone ? <a href={`tel:+91${u.phone}`} className="underline">+91 {u.phone}</a> : "—"}</td><td className="p-3"><span className="chip">{u.role}</span></td><td className="p-3">{u.status}</td>
            <td className="p-3">{u.role !== "admin" && <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => patch(u, { status: u.status === "active" ? "suspended" : "active" })} data-testid={`user-toggle-status-${u.email}`}>{u.status === "active" ? "Suspend" : "Activate"}</Button>
              {["customer", "moderator"].includes(u.role) && <Button size="sm" variant="ghost" onClick={() => patch(u, { role: u.role === "customer" ? "moderator" : "customer" })} data-testid={`user-toggle-role-${u.email}`}>Make {u.role === "customer" ? "moderator" : "customer"}</Button>}
              <Button size="sm" variant="ghost" onClick={() => resetPw(u)} data-testid={`user-reset-password-${u.email}`}>Reset password</Button>
            </div>}</td></tr>
        ))}</tbody>
      </table>
    </div>
    </div>
  );
};

export const Activities = () => {
  const [items, load] = useAdminList("/admin/activities");
  const [f, setF] = useState({ slug: "", name_en: "", name_hi: "" });
  const add = async () => {
    try {
      await api.post("/admin/activities", f);
      setF({ slug: "", name_en: "", name_hi: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      <div className="card-surface divide-y">{items === null ? <div className="p-4"><Spin /></div> : items.map((a) => (
        <div key={a.slug} className="p-4 flex items-center justify-between text-sm"><span><b>{a.name_en}</b> · {a.name_hi} <span className="font-mono text-xs text-slate-400">{a.slug}</span></span>
          <Switch checked={a.active} onCheckedChange={(v) => api.patch(`/admin/activities/${a.slug}`, { active: v }).then(load)} data-testid={`activity-toggle-${a.slug}`} /></div>
      ))}</div>
      <div className="card-surface p-4 grid sm:grid-cols-4 gap-2">
        <Input placeholder="slug (e.g. gym)" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="activity-slug-input" />
        <Input placeholder="Name (English)" value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} data-testid="activity-en-input" />
        <Input placeholder="नाम (हिंदी)" value={f.name_hi} onChange={(e) => setF({ ...f, name_hi: e.target.value })} data-testid="activity-hi-input" />
        <Button onClick={add} data-testid="activity-add-btn">Add activity</Button>
      </div>
    </div>
  );
};

const FIELDS = [["commission_rate", "Platform commission (%) — companion gets the rest"], ["min_hourly", "Min hourly price (₹)"], ["max_hourly", "Max hourly price (₹)"], ["full_refund_hours", "Full refund if cancelled ≥ X hours before"], ["partial_refund_hours", "Partial refund if cancelled ≥ X hours before"], ["partial_refund_pct", "Partial refund (%)"]];

export const Settings = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  const save = async () => {
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, Number(s[k])]));
      body.gst_rate = 0;
      body.reminder_hours = Number(s.reminder_hours);
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!s) return <Spin />;
  return (
    <div className="card-surface p-6 max-w-xl space-y-4" data-testid="admin-settings">
      {FIELDS.map(([k, l]) => <label key={k} className="flex items-center justify-between gap-4 text-sm"><span>{l}</span><Input type="number" className="w-28" value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`setting-${k}`} /></label>)}
      <div className="flex items-center justify-between gap-4 text-sm">
        <span>Send meetup reminder (hours before start)</span>
        <Select value={String(s.reminder_hours)} onValueChange={(v) => setS({ ...s, reminder_hours: Number(v) })}>
          <SelectTrigger className="w-28" data-testid="setting-reminder_hours"><SelectValue /></SelectTrigger>
          <SelectContent>{[1, 2, 3, 6, 12, 24].map((h) => <SelectItem key={h} value={String(h)} data-testid={`reminder-option-${h}`}>{h} {h === 1 ? "hour" : "hours"}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <p className="text-xs text-slate-500" data-testid="admin-gateway-status">Payment method: <b>Cash only</b>. Online payments are disabled. GST is not charged — confirm tax treatment with a CA.</p>
      <Button onClick={save} className="rounded-full" data-testid="settings-save-btn">Save settings</Button>
    </div>
  );
};
